import React, { useMemo, useState } from "react";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { sortPartsTableData } from "./sortPartsTableData";
import PartMentionsPopup from "../../../../common/AppSpecificConpnents/PartsTable/PartMentionsPopup";

/**
 * PartsTable Component
 *
 * Reusable table component for displaying parts/BOM data
 * Handles both BOM table format and step parts_required format
 *
 * @param {Array} data - Array of parts data
 * @param {string} itemCodeColumnTitle - Custom title for "Item Code" column (default: "Item Code")
 * @param {boolean} autoSort - Whether to automatically sort the data (default: true)
 * @param {boolean} hideZeroQty - Whether to hide qty when it's 0 (default: false)
 * @param {string} dataFormat - "bom" or "step" format (default: "bom")
 */
const PartsTable = ({
  data = [],
  itemCodeColumnTitle = "Item Code",
  autoSort = true,
  hideZeroQty = false,
  dataFormat = "bom", // "bom" or "step"
  totalQtyToBuild = null,
}) => {
  // Sort data if autoSort is enabled
  const processedData = autoSort ? sortPartsTableData(data, dataFormat) : data;
  const [whereUsedPart, setWhereUsedPart] = useState(null);

  /**
   * Extract field values based on data format
   * Handles different data structures (BOM vs Step)
   */
  const getFieldValue = (row, field) => {
    if (dataFormat === "step") {
      // Step format: nested in part_info
      switch (field) {
        case "type":
          return (
            row.part_info?.type_info?.name ||
            row.part_info?.type_info?.type_name ||
            row.part_info?.type_name ||
            ""
          );
        case "partNumber":
          return row.part_info?.part_number || "";
        case "description":
          return row.part_info?.description || "";
        case "table_note":
          return row.part_info?.table_note || "";
        case "qty":
          return row.qty;
        case "unit":
          return row.part_info?.unit_info?.unit_name || "";
        default:
          return "";
      }
    } else {
      // BOM format: flat structure
      switch (field) {
        case "type":
          return row.type_name || "";
        case "partNumber":
          return row.part_number || "";
        case "description":
          return row.part_description || "";
        case "table_note":
          return row.table_note || "";
        case "qty":
          return row.qty;
        case "unit":
          return row.unit_name || "";
        default:
          return "";
      }
    }
  };

  const preparePartForPopup = useMemo(
    () => (row) => {
      if (!row) return null;

      if (dataFormat === "step") {
        const partInfo = row.part_info || {};
        return {
          ...partInfo,
          item_code: partInfo.part_number || partInfo.item_code,
          part_number: partInfo.part_number || partInfo.item_code,
          part_id: partInfo.part_id ?? row.part_id,
        };
      }

      return {
        ...row,
        item_code: row.part_number || row.item_code,
        part_number: row.part_number || row.item_code,
        part_id: row.part_id,
      };
    },
    [dataFormat]
  );

  const hasTotalQtyColumn =
    Number.isFinite(totalQtyToBuild) && Number(totalQtyToBuild) >= 0;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Type</th>
          <th>{itemCodeColumnTitle}</th>
          <th>Description</th>
          <th>Qty Per</th>
          {hasTotalQtyColumn && <th>Total Qty to build {totalQtyToBuild}</th>}
          <th>Unit</th>
        </tr>
      </thead>
      <tbody>
        {processedData.length === 0 ? (
          <tr>
            <td colSpan={hasTotalQtyColumn ? 6 : 5} style={{ textAlign: "center" }}>
              No parts to display.
            </td>
          </tr>
        ) : (
          processedData.map((row, index) => {
            const qty = getFieldValue(row, "qty");
            const displayQty = hideZeroQty && qty === 0 ? "" : qty;
            const totalQty = hasTotalQtyColumn
              ? (Number(qty) || 0) * Number(totalQtyToBuild)
              : null;
            const table_note = getFieldValue(row, "table_note");

            return (
              <tr key={index}>
                <td>{getFieldValue(row, "type")}</td>
                <td>{getFieldValue(row, "partNumber")}</td>
                <td>
                  <RenderHtml content={getFieldValue(row, "description")} />
                  {table_note && (
                    <div style={{ marginTop: "4px" }}>
                      <RenderHtml content={table_note} />
                    </div>
                  )}
                </td>
                <td>{displayQty}</td>
                {hasTotalQtyColumn && <td>{totalQty}</td>}
                <td>{getFieldValue(row, "unit")}</td>
              </tr>
            );
          })
        )}
      </tbody>
      {whereUsedPart && (
        <PartMentionsPopup
          part={whereUsedPart}
          onClose={() => setWhereUsedPart(null)}
        />
      )}
    </table>
  );
};

export default PartsTable;
